import { useCallback, useMemo } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import BodyText from 'LuxKit/Typography/BodyText'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import focusStyle from 'styles/utils/focusStyle'

const PAID_SEARCH_DESKTOP_QUICK_SELECT_INPUTS_NAME = 'paid-search-desktop-quick-select'

export const PAID_SEARCH_DESKTOP_QUICK_SELECT_SORTS = ['recommended', 'value', 'customerRating', 'price.asc'] as const

export type PaidSearchDesktopQuickSelectSort = typeof PAID_SEARCH_DESKTOP_QUICK_SELECT_SORTS[number]

function isPaidSearchDesktopQuickSelectSort(value: string): value is PaidSearchDesktopQuickSelectSort {
  return PAID_SEARCH_DESKTOP_QUICK_SELECT_SORTS.some(sort => sort === value)
}

export function getPaidSearchDesktopQuickSelectActiveSort(
  windowSearch: string,
): PaidSearchDesktopQuickSelectSort | undefined {
  const sortBy = parseSearchString(windowSearch).sortBy
  if (!sortBy || sortBy === 'recommended') return 'recommended'
  if (isPaidSearchDesktopQuickSelectSort(sortBy)) return sortBy
  return undefined
}

export function buildPaidSearchDesktopQuickSelectSearch(
  windowSearch: string,
  sort: PaidSearchDesktopQuickSelectSort,
): string {
  const nextParams = new URLSearchParams(windowSearch)
  if (sort === 'recommended') {
    nextParams.delete('sortBy')
  } else {
    nextParams.set('sortBy', sort)
  }
  return nextParams.toString()
}

const QuickSelect = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-inline-size: 0;
`

const Legend = styled(BodyText)`
  margin-block-end: ${rem(8)};
`

const Track = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(max-content, 1fr);
  gap: ${rem(4)};
  padding: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  body.${REBRAND_PALETTE_CLASS} & {
    background-color: var(--palette-neutral-surface-light-background-alt);
  }
`

const Option = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-inline-size: ${rem(80)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.round};
  cursor: pointer;
  text-align: center;

  &[data-selected='true'] {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    box-shadow: ${props => props.theme.shadow.flat.medium};
  }

  &:has(input:focus-visible) {
    ${focusStyle}
  }
`

const Radio = styled.input`
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`

interface Props {
  windowSearch: string
  onSortChange: SortControlChangeHandler
}

function PaidSearchDesktopQuickSelect(props: Props) {
  const { windowSearch, onSortChange } = props
  const intl = useIntl()

  const labels = useMemo(() => ({
    recommended: intl.formatMessage({
      id: '8UkjFFxXsr1pDqaIuNpD',
      defaultMessage: 'Our top picks',
      description: 'Quick select label that applies the recommended sort on desktop paid search accommodation results',
    }),
    'price.asc': intl.formatMessage({
      id: 'RBS6sgHxDPfhW4aEWoJD',
      defaultMessage: 'Lowest price first',
      description: 'Quick select label that sorts desktop paid search accommodation results from the lowest price',
    }),
    value: intl.formatMessage({
      id: 'aFnsuMtTHRu4TMiEIKuQ',
      defaultMessage: 'Best value',
      description: 'Quick select label that applies the existing best value sort on desktop paid search accommodation results',
    }),
    customerRating: intl.formatMessage({
      id: '7HbVSU0nUrVz8bB98YNv',
      defaultMessage: 'Top reviewed',
      description: 'Quick select label that applies the customer rating sort on desktop paid search accommodation results',
    }),
  }), [intl])

  const exploreByLabel = intl.formatMessage({
    id: 'VVThbJHIl2dOXc28OUYG',
    defaultMessage: 'Explore by',
    description: 'Heading above the four sort shortcuts on desktop paid search accommodation search results',
  })

  const activeSort = getPaidSearchDesktopQuickSelectActiveSort(windowSearch)

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget
    if (!isPaidSearchDesktopQuickSelectSort(value)) return
    onSortChange({ search: buildPaidSearchDesktopQuickSelectSearch(windowSearch, value) })
  }, [onSortChange, windowSearch])

  const options = useMemo(() => PAID_SEARCH_DESKTOP_QUICK_SELECT_SORTS.map((sort) => {
    const checked = activeSort === sort
    return (
      <Option key={sort} data-selected={checked ? 'true' : 'false'}>
        <Radio
          type="radio"
          name={PAID_SEARCH_DESKTOP_QUICK_SELECT_INPUTS_NAME}
          value={sort}
          checked={checked}
          onChange={handleChange}
        />
        <BodyText as="span" variant="medium" weight={checked ? 'bold' : 'normal'}>
          {labels[sort]}
        </BodyText>
      </Option>
    )
  }), [activeSort, handleChange, labels])

  return (
    <QuickSelect data-testid="paid-search-desktop-quick-select" role="radiogroup">
      <Legend as="legend" variant="medium" weight="bold">{exploreByLabel}</Legend>
      <Track>
        {options}
      </Track>
    </QuickSelect>
  )
}

export default PaidSearchDesktopQuickSelect
